import React, { useState, useCallback, memo } from 'react';
import { FormFieldsWrapper, HelpDeskFormWrapper } from './styled';
import { ButtonGroup } from '@atlaskit/button';
import { Button } from 'view/common/analytics/button';
import TextField from '@atlaskit/textfield';
import Form, { ErrorMessage, Field, FormFooter, FormSection, HelperMessage } from '@atlaskit/form';
import TextArea from '@atlaskit/textarea';
import messages from './messages';
import { useIntl } from 'react-intl-next';
import { history } from 'util/history';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { usePathParam } from 'react-resource-router';
import { token } from '@atlaskit/tokens';
import { NavigationPrompt } from 'view/common/prompt';
import Heading from '@atlaskit/heading';
import Tooltip from '@atlaskit/tooltip';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { RemoveHelpDesk } from '../remove-help-desk';
import { isUnifiedHelpRemoveHelpDeskEnabled } from 'feature-flags';

export const NAME_MAX_LENGTH = 100;
export const DESC_MAX_LENGTH = 1000;
export const CONTACT_INFO_MAX_LENGTH = 500;

const EXTERNAL_LINK_REGEX =
    // eslint-disable-next-line no-useless-escape
    /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/|ftp:\/\/www\.|ftp:\/\/|tel:\/\/www\.|tel:\/\/|mailto:\/\/www\.|mailto:\/\/)?[a-z0-9]+([\-\.][a-z0-9]+)*\.[a-z]{2,5}(:\d{1,5})?(\/.*)?$/;
// eslint-disable-next-line no-useless-escape
const EMAIL_PATTERN = /.+\@.+\..+/;
export interface FormData {
    name: string;
    description: string;
    contactInfo: string;
}
export interface HelpDeskFormProps {
    title: string;
    description: string;
    ctaText: string;
    defaultValues?: FormData;
    onSubmit: (data: FormData) => Promise<unknown>;
}

const onCancelHandler = () => history.push('/');

const HelpDeskForm: React.FC<HelpDeskFormProps> = ({ title, description, ctaText, defaultValues, onSubmit }) => {
    const [helpDeskId] = usePathParam('helpDeskId');
    const [contactInfoError, setContactInfoError] = useState('');

    const { formatMessage } = useIntl();

    const validateName = useCallback(
        (value: string | undefined) => {
            if (value) {
                return value?.trim().length < NAME_MAX_LENGTH
                    ? undefined
                    : formatMessage(messages.helpDeskFormNameLimitExceedError, {
                          value: NAME_MAX_LENGTH,
                      });
            }
            return formatMessage(messages.helpDeskFormNameEmptyError);
        },
        [formatMessage]
    );

    const validateDescription = useCallback(
        (value: string | undefined) => {
            if (value) {
                return value?.trim().length < DESC_MAX_LENGTH
                    ? undefined
                    : formatMessage(messages.helpDeskFormDescriptionLimitExceedError, {
                          value: DESC_MAX_LENGTH,
                      });
            }
            return formatMessage(messages.helpDeskFormDescriptionEmptyError);
        },
        [formatMessage]
    );

    const validateContactInfo = useCallback(
        (value: string | undefined) => {
            if (!value) {
                setContactInfoError('');
                return formatMessage(messages.helpDeskFormContactInfoEmptyError);
            }
            if (EMAIL_PATTERN.test(value) || EXTERNAL_LINK_REGEX.test(value)) {
                setContactInfoError('');
            }
            return undefined;
        },
        [formatMessage]
    );

    const handleContactInfoValidation = useCallback(
        (contactInfo: string) => {
            if (contactInfo.trim().length > CONTACT_INFO_MAX_LENGTH) {
                return formatMessage(messages.helpDeskFormContactInfoLimitExceedError, {
                    value: CONTACT_INFO_MAX_LENGTH,
                });
            }
            if (!(EMAIL_PATTERN.test(contactInfo) || EXTERNAL_LINK_REGEX.test(contactInfo))) {
                return formatMessage(messages.helpDeskFormContactInfoInvalidError);
            }
            return '';
        },
        [formatMessage]
    );

    const handleContactInfoBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            setContactInfoError(handleContactInfoValidation(e.target.value));
        },
        [handleContactInfoValidation]
    );

    const handleSubmit = useCallback(
        async (data: FormData) => {
            const error = handleContactInfoValidation(data.contactInfo);
            if (error) {
                setContactInfoError(error);
                return;
            }
            await onSubmit(data);
        },
        [handleContactInfoValidation, onSubmit]
    );

    const navigationPrompt = useCallback(
        (submitting: boolean, dirty: boolean) => <NavigationPrompt preventNavigation={!submitting && dirty} />,
        []
    );

    return (
        <HelpDeskFormWrapper>
            <Form<FormData> onSubmit={handleSubmit}>
                {({ formProps, submitting, dirty }) => (
                    <form {...formProps}>
                        <Box
                            xcss={xcss({
                                marginBottom: token('space.200', '16px'),
                            })}
                        >
                            <Inline spread="space-between">
                                <Heading level="h700">{title}</Heading>
                            </Inline>
                        </Box>
                        <p aria-hidden="true">{description}</p>
                        <FormSection>
                            <FormFieldsWrapper>
                                <Field
                                    aria-required
                                    name="name"
                                    defaultValue={defaultValues?.name}
                                    label={formatMessage(messages.helpDeskFormNameLabel)}
                                    validate={validateName}
                                    isRequired
                                >
                                    {({ fieldProps, error }) => (
                                        <>
                                            <TextField
                                                autoComplete="off"
                                                {...fieldProps}
                                                placeholder={formatMessage(messages.helpDeskFormNamePlaceholder)}
                                            />
                                            <HelperMessage>
                                                <Inline space="space.025">
                                                    {formatMessage(messages.helpDeskFormTitleHelperText)}
                                                    <Tooltip
                                                        content={formatMessage(messages.helpDeskFormTitleHelperTooltip)}
                                                        position="right"
                                                        analyticsContext={{
                                                            actionSubjectId: helpDeskId
                                                                ? 'update-help-desk-title'
                                                                : 'create-help-desk-title',
                                                        }}
                                                    >
                                                        <InfoIcon label="" size="small" />
                                                    </Tooltip>
                                                </Inline>
                                            </HelperMessage>
                                            {error && <ErrorMessage>{error}</ErrorMessage>}
                                        </>
                                    )}
                                </Field>

                                <Field
                                    name="description"
                                    label={formatMessage(messages.helpDeskFormDescLabel)}
                                    validate={validateDescription}
                                    defaultValue={defaultValues?.description}
                                    isRequired
                                >
                                    {({ fieldProps, error }) => (
                                        <>
                                            <TextArea
                                                autoComplete="off"
                                                {...fieldProps}
                                                // eslint-disable-next-line react/jsx-no-bind
                                                onChange={(event) => {
                                                    const value = event.target.value;
                                                    fieldProps.onChange(value);
                                                }}
                                                placeholder={formatMessage(messages.helpDeskFormDescPlaceholder)}
                                                minimumRows={6}
                                            />
                                            <HelperMessage>
                                                <Inline space="space.025">
                                                    {formatMessage(messages.helpDeskFormDescHelperText)}
                                                    <Tooltip
                                                        content={formatMessage(messages.helpDeskFormDescHelperTooltip)}
                                                        position="right"
                                                        analyticsContext={{
                                                            actionSubjectId: helpDeskId
                                                                ? 'update-help-desk-description'
                                                                : 'create-help-desk-description',
                                                        }}
                                                    >
                                                        <InfoIcon label="" size="small" />
                                                    </Tooltip>
                                                </Inline>
                                            </HelperMessage>
                                            {error && <ErrorMessage>{error}</ErrorMessage>}
                                        </>
                                    )}
                                </Field>

                                <Field
                                    name="contactInfo"
                                    label={formatMessage(messages.helpDeskFormContactInfoLabel)}
                                    validate={validateContactInfo}
                                    defaultValue={defaultValues?.contactInfo}
                                    isRequired
                                >
                                    {({ fieldProps, error }) => (
                                        <>
                                            <TextField
                                                autoComplete="off"
                                                {...fieldProps}
                                                placeholder={formatMessage(messages.helpDeskFormContactInfoPlaceholder)}
                                                onBlur={handleContactInfoBlur}
                                            />
                                            {error ? (
                                                <ErrorMessage>{error}</ErrorMessage>
                                            ) : (
                                                contactInfoError && <ErrorMessage>{contactInfoError}</ErrorMessage>
                                            )}
                                        </>
                                    )}
                                </Field>
                            </FormFieldsWrapper>
                        </FormSection>
                        <FormFooter align="start">
                            <ButtonGroup>
                                <Button
                                    actionSubjectId={helpDeskId ? 'update-help-desk' : 'create-help-desk'}
                                    type="submit"
                                    appearance="primary"
                                    isLoading={submitting}
                                >
                                    {ctaText}
                                </Button>
                                <Button
                                    actionSubjectId={helpDeskId ? 'cancel-update-help-desk' : 'cancel-create-help-desk'}
                                    onClick={onCancelHandler}
                                >
                                    {formatMessage(messages.helpDeskFormCancelButton)}
                                </Button>
                            </ButtonGroup>
                        </FormFooter>
                        {helpDeskId && isUnifiedHelpRemoveHelpDeskEnabled() && (
                            <RemoveHelpDesk helpDeskId={helpDeskId} helpDeskName={defaultValues?.name} />
                        )}
                        {/* this prevents rerendering on cancel button click */}
                        {navigationPrompt(submitting, dirty)}
                    </form>
                )}
            </Form>
        </HelpDeskFormWrapper>
    );
};

export default memo(HelpDeskForm);
